/**
 * The deal value range the backend will accept.
 *
 * MIN_DEAL_VALUE mirrors the Django setting of the same name. Every endpoint that
 * takes a deal value validates against it — user-info at step 0, review-offer, and
 * the simulator — so a screen that offers less is offering something the API will
 * reject. Three of them did: the auto deals page and both review-offer screens
 * allowed $5,000, which step 0 has always refused.
 *
 * It is a mirrored constant rather than a fetched one, because the backend does not
 * expose its settings to clients. If MIN_DEAL_VALUE is ever changed in the
 * environment, this has to change with it.
 */
export const MIN_DEAL_VALUE = 20000;

/**
 * MAX_DEAL_VALUE mirrors the largest ConsortiumGroup.group_value. No endpoint
 * validates against it — the limit is enforced by club allocation instead, and it
 * fails quietly: match_club_for_deal_value keeps only clubs worth at least the deal
 * value, so above the largest club it matches nothing and the simulator returns a
 * correct quote with a null clubId. The flow then has no club to join and bounces
 * back to the simulator without a word. A screen that offers more is offering a
 * deal that dies at Join Now.
 *
 * It was 135000 while the largest club was 40k, so the auto screens advertised
 * roughly three times what could actually be fulfilled. Real estate is not affected:
 * realStateSimulateSchema on the deals page carries its own $100k–$1M range.
 *
 * Raise this whenever a larger club is created.
 */
export const MAX_DEAL_VALUE = 40000;

const asCurrency = (value: number) => `$${value.toLocaleString('en-US')}`;

export const DEAL_VALUE_RANGE_MESSAGE = `The value must be between ${asCurrency(
  MIN_DEAL_VALUE
)} and ${asCurrency(MAX_DEAL_VALUE)}`;

export const isDealValueInRange = (value: number) =>
  value >= MIN_DEAL_VALUE && value <= MAX_DEAL_VALUE;
